import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Plus, Trash2, ExternalLink, Search, AlertTriangle, Sparkles,
  Eye, EyeOff, ArrowUpRight, FolderOpen, Settings, X, Check,
} from "lucide-react";
import { PageSkeleton, TableRowsSkeleton } from "@/components/LoadingSkeleton";
import OnboardingTable from "@/components/onboarding/OnboardingTable";

const STATUS_CHIP_CLS = {
  slate:   "bg-slate-100 text-slate-700 border-slate-200",
  amber:   "bg-amber-100 text-amber-800 border-amber-200",
  sky:     "bg-sky-100 text-sky-800 border-sky-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rose:    "bg-rose-100 text-rose-800 border-rose-200",
  violet:  "bg-violet-100 text-violet-800 border-violet-200",
  indigo:  "bg-purple-100 text-purple-800 border-purple-200",
};

function TextCell({ value, onSave, editable, testId, secret = false, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [reveal, setReveal] = useState(false);
  useEffect(() => setVal(value ?? ""), [value]);

  const commit = () => {
    setEditing(false);
    if ((val || "") !== (value || "")) onSave(val);
  };

  if (editing) {
    // Iter 36.9.10 — When `secret` (password column), browser & 3rd-party
    // password managers (LastPass, 1Password, iCloud Keychain, Chrome) treat
    // the input as a login form. On paste they inject the CURRENTLY LOGGED-IN
    // user's email into the nearest sibling text field — which happens to be
    // the SEO Onboarding search box, causing the row to filter itself out.
    // Blocking autofill hooks fixes the paste-hijack:
    //   • `autoComplete="new-password"`  → strongest "don't autofill" hint.
    //   • non-standard `name`            → avoids the "password" heuristic.
    //   • `data-lpignore`, `data-1p-ignore`, `data-form-type="other"`
    //     → opt-out for LastPass / 1Password / Bitwarden respectively.
    return (
      <Input
        autoFocus
        type={secret && !reveal ? "password" : "text"}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); } }}
        className="h-7 text-xs px-2"
        data-testid={`${testId}-input`}
        {...(secret ? {
          autoComplete: "new-password",
          name: `pluto-seo-secret-${testId}`,
          "data-lpignore": "true",
          "data-1p-ignore": true,
          "data-form-type": "other",
        } : {})}
      />
    );
  }

  const display = value ? (secret && !reveal ? "•".repeat(Math.min(10, (value || "").length || 6)) : value) : <span className="text-slate-300 italic">—</span>;

  return (
    <div className={`flex items-center gap-1 group ${className}`}>
      <span
        onClick={() => editable && setEditing(true)}
        className={`min-h-[24px] flex-1 px-1.5 py-0.5 rounded text-xs ${editable ? "cursor-pointer hover:bg-purple-50 hover:ring-1 hover:ring-purple-200" : ""} truncate`}
        title={editable ? "Click to edit" : ""}
        data-testid={testId}
      >
        {display}
      </span>
      {secret && value && (
        <button onClick={() => setReveal((v) => !v)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700">
          {reveal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

function DropdownCell({ value, options, onSave, editable, testId }) {
  const opt = options.find((o) => o.key === value);
  return (
    <Select value={value || ""} onValueChange={(v) => onSave(v)} disabled={!editable}>
      <SelectTrigger className={`h-7 text-xs px-2 border-slate-200 bg-transparent min-w-[110px] ${!editable ? "opacity-70" : ""}`} data-testid={testId}>
        <SelectValue placeholder={<span className="text-slate-300 italic">—</span>}>
          {opt ? opt.label : <span className="text-slate-300 italic">—</span>}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.key} value={o.key} data-testid={`${testId}-opt-${o.key}`}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StatusCell({ value, options, onSave, editable, testId }) {
  const opt = options.find((o) => o.key === value);
  const chipCls = STATUS_CHIP_CLS[opt?.color || "slate"] || STATUS_CHIP_CLS.slate;
  return (
    <Select value={value || ""} onValueChange={(v) => onSave(v)} disabled={!editable}>
      <SelectTrigger
        className={`h-7 text-[11px] px-2 border rounded-md font-medium ${chipCls} min-w-[130px] ${!editable ? "opacity-70" : ""}`}
        data-testid={testId}
      >
        <SelectValue placeholder={<span className="italic">—</span>}>
          {opt ? opt.label : <span className="italic">—</span>}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.key} value={o.key} data-testid={`${testId}-opt-${o.key}`}>
            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_CHIP_CLS[o.color] || STATUS_CHIP_CLS.slate}`}>{o.label}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function IncubationCell_REMOVED_ITER43_() { return null; }

function ManageFolderDialog({ folder, open, onClose, onSaved }) {
  const [statusOptions, setStatusOptions] = useState(folder?.status_options || []);
  const [hostedOptions, setHostedOptions] = useState(folder?.hosted_on_options || []);
  const [name, setName] = useState(folder?.name || "");
  const [type, setType] = useState(folder?.type || "custom");
  const [kpCols, setKpCols] = useState(folder?.kp_editable_columns || ["keyword", "niche"]);
  const [saving, setSaving] = useState(false);

  const ALL_TASK_COLS = ["keyword", "niche", "ai_score", "draft", "method", "writer_id", "publisher_id", "status", "check", "notes", "docs", "date"];

  useEffect(() => {
    setName(folder?.name || "");
    setStatusOptions(folder?.status_options || []);
    setHostedOptions(folder?.hosted_on_options || []);
    setType(folder?.type || "custom");
    setKpCols(folder?.kp_editable_columns || ["keyword", "niche"]);
  }, [folder, open]);

  const addStatus = () => setStatusOptions([...statusOptions, { key: `custom_${Date.now()}`, label: "New status", color: "slate" }]);
  const addHosted = () => setHostedOptions([...hostedOptions, { key: `custom_${Date.now()}`, label: "New provider" }]);
  const removeStatus = (i) => setStatusOptions(statusOptions.filter((_, x) => x !== i));
  const removeHosted = (i) => setHostedOptions(hostedOptions.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      const patch = { name, status_options: statusOptions, hosted_on_options: hostedOptions, type, kp_editable_columns: kpCols };
      const r = await api.patch(`/seo/folders/${folder.id}`, patch);
      toast.success("Folder saved");
      onSaved(r.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="folder-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> Manage {folder?.name}</DialogTitle>
          <DialogDescription>Edit folder name, status values, and dropdown options. Admin / Department Admin only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Folder name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="folder-name-input" />
          </div>

          <div>
            <Label className="text-xs">Folder type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-9" data-testid="folder-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="onboarding">Onboarding (site login table)</SelectItem>
                <SelectItem value="keyword_planning">Keyword Planning (Content projects)</SelectItem>
                <SelectItem value="custom">Custom / Placeholder</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "keyword_planning" && (
            <div className="border border-fuchsia-200 bg-fuchsia-50/50 rounded-md p-3 space-y-1">
              <Label className="text-xs">Columns editable by Keyword Planning members</Label>
              <div className="text-[11px] text-slate-500 mb-1">Members shared to this folder can edit ONLY these task columns on Content projects. Everything else is read-only for them.</div>
              <div className="flex flex-wrap gap-1.5">
                {ALL_TASK_COLS.map((c) => {
                  const on = kpCols.includes(c);
                  return (
                    <button
                      key={c}
                      onClick={() => setKpCols(on ? kpCols.filter((x) => x !== c) : [...kpCols, c])}
                      data-testid={`kp-col-${c}`}
                      className={`text-[11px] px-2 py-0.5 rounded border transition ${on ? "bg-fuchsia-600 text-white border-fuchsia-700" : "bg-white text-slate-600 border-slate-200 hover:border-fuchsia-300"}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Status options</Label>
              <Button size="sm" variant="outline" onClick={addStatus} className="h-7 text-xs" data-testid="add-status-btn"><Plus className="h-3 w-3 mr-1" /> Add status</Button>
            </div>
            <div className="mt-1 space-y-1 max-h-[240px] overflow-y-auto">
              {statusOptions.map((o, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input value={o.label} onChange={(e) => { const c=[...statusOptions]; c[i]={...c[i],label:e.target.value, key: c[i].key || re(e.target.value)}; setStatusOptions(c); }} className="h-7 text-xs flex-1" data-testid={`status-label-${i}`} />
                  <Select value={o.color || "slate"} onValueChange={(v) => { const c=[...statusOptions]; c[i]={...c[i], color: v}; setStatusOptions(c); }}>
                    <SelectTrigger className="h-7 w-24 text-[11px]" data-testid={`status-color-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS_CHIP_CLS).map((k) => <SelectItem key={k} value={k}><span className={`inline-block px-1 py-0.5 rounded text-[10px] ${STATUS_CHIP_CLS[k]}`}>{k}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => removeStatus(i)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`status-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {statusOptions.length === 0 && <div className="text-xs text-slate-400 italic">No statuses configured.</div>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Hosted-on options</Label>
              <Button size="sm" variant="outline" onClick={addHosted} className="h-7 text-xs" data-testid="add-hosted-btn"><Plus className="h-3 w-3 mr-1" /> Add value</Button>
            </div>
            <div className="mt-1 space-y-1 max-h-[200px] overflow-y-auto">
              {hostedOptions.map((o, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input value={o.label} onChange={(e) => { const c=[...hostedOptions]; c[i]={...c[i], label:e.target.value, key: c[i].key || re(e.target.value)}; setHostedOptions(c); }} className="h-7 text-xs flex-1" data-testid={`hosted-label-${i}`} />
                  <button onClick={() => removeHosted(i)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`hosted-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {hostedOptions.length === 0 && <div className="text-xs text-slate-400 italic">No values configured.</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="folder-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function re(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 30) || `k_${Date.now()}`; }

function KeywordPlanningView({ folder }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u, r] = await Promise.all([
        api.get("/projects"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/keyword-requests?status=pending").catch(() => ({ data: [] })),
      ]);
      setProjects((p.data || []).filter((x) => (x.department || "content") === "content"));
      setUsers(u.data || []);
      setRequests(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isAdminOrSeoDA = (user?.role === "admin" || user?.role === "super_admin") || (user?.role === "department_admin" && (user?.departments || []).includes("seo"));

  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const contentDAs = useMemo(() => users.filter((u) => u.role === "department_admin" && (u.departments || [u.department] || []).includes("content")), [users]);
  const contentDAString = contentDAs.map((u) => u.name).join(", ") || "—";

  const requestedProjectIds = useMemo(() => new Set(requests.map((r) => r.project_id)), [requests]);

  const activeProjects = useMemo(() => projects.filter((p) => showArchived || !p.archived), [projects, showArchived]);

  const filtered = useMemo(() => {
    let list = activeProjects;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((x) => (x.name || "").toLowerCase().includes(s));
    }
    return list;
  }, [activeProjects, q]);

  const archivedCount = useMemo(() => projects.filter((p) => p.archived).length, [projects]);

  const resolveRequest = async (id) => {
    try {
      await api.post(`/keyword-requests/${id}/resolve`);
      setRequests((all) => all.filter((r) => r.id !== id));
      toast.success("Marked resolved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleKwAssigned = async (proj) => {
    try {
      const r = await api.post(`/projects/${proj.id}/kw-assigned`);
      setProjects((all) => all.map((x) => (x.id === proj.id ? { ...x, ...r.data } : x)));
      // Refresh requests list since assigning auto-resolves pending ones
      if (!proj.kw_assigned) {
        setRequests((all) => all.filter((rq) => rq.project_id !== proj.id));
      }
      toast.success(!proj.kw_assigned ? "Marked KW Assigned · archived" : "Unarchived");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteProject = async (proj) => {
    if (!window.confirm(`Delete project "${proj.name}"? All its tasks will be removed too.`)) return;
    try {
      await api.delete(`/projects/${proj.id}`);
      setProjects((all) => all.filter((x) => x.id !== proj.id));
      toast.success("Project deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const namesForIds = (ids) => (ids || []).map((id) => userById[id]?.name).filter(Boolean).join(", ") || "—";

  return (
    <div className="space-y-4">
      {/* Keyword request shake banner */}
      {requests.length > 0 && (
        <div className="shake-animation rounded-lg border border-red-300 bg-red-50 px-4 py-3 space-y-2" data-testid="kw-request-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 animate-pulse" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-900">These projects need keywords ({requests.length})</div>
              <div className="text-xs text-red-700 mt-0.5">Requested by Content team or auto-added after SEO incubation ended.</div>
            </div>
          </div>
          <ul className="divide-y divide-red-200 border-t border-red-200 max-h-[240px] overflow-y-auto">
            {requests.map((rq) => (
              <li key={rq.id} className="py-2 flex items-center gap-3" data-testid={`kw-req-${rq.id}`}>
                <FolderOpen className="h-4 w-4 text-red-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{rq.project_name}</div>
                  <div className="text-[11px] text-red-600">
                    {rq.source === "seo_push" ? "Auto — pushed from SEO" : `Requested by ${rq.requested_by_name}`}
                    {rq.note ? <em className="text-red-500"> · “{rq.note}”</em> : null}
                  </div>
                </div>
                <Link to={`/projects/${rq.project_id}`} className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded font-semibold inline-flex items-center gap-1" data-testid={`kw-open-${rq.project_id}`}>
                  Open sheet <ExternalLink className="h-3 w-3" />
                </Link>
                <button onClick={() => resolveRequest(rq.id)} title="Mark resolved" className="text-slate-400 hover:text-emerald-600 p-1" data-testid={`kw-resolve-${rq.id}`}>
                  <Check className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Search + toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Content projects…" className="pl-9" data-testid="kp-search" />
        </div>
        <label className={`inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border cursor-pointer transition ${showArchived ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-emerald-600" data-testid="kp-show-archived" />
          Show archived {archivedCount > 0 && <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-1 rounded">{archivedCount}</span>}
        </label>
        <div className="text-xs text-slate-500 ml-auto">{filtered.length} of {activeProjects.length}{showArchived ? ` (incl. archived)` : ""}</div>
      </div>

      {/* Projects table — scrollable both dimensions; only the table scrolls, not the page */}
      <div className="bg-white rounded-lg border border-slate-200 max-w-full">
        <div className="overflow-auto max-h-[calc(100vh-360px)] rounded-lg">
          <table className="text-sm" style={{ minWidth: "max-content" }}>
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 min-w-[220px]">Project</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Team Lead</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Content Manager</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dept Admin</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Flags</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 min-w-[220px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <TableRowsSkeleton cols={6} rows={5} firstAvatar />}
              {!loading && filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-400">No Content projects visible.</td></tr>}
              {!loading && filtered.map((p) => (
                <tr key={p.id} className={`hover:bg-slate-50/60 ${p.archived ? "bg-emerald-50/30 opacity-80" : ""}`} data-testid={`kp-proj-${p.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-purple-500 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{p.name}</div>
                        {p.site_url && <div className="text-[11px] text-slate-500 truncate">{p.site_url}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{namesForIds(p.team_lead_ids)}</td>
                  <td className="px-3 py-2 text-slate-700">{namesForIds(p.content_manager_ids)}</td>
                  <td className="px-3 py-2 text-slate-700">{contentDAString}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {p.kw_assigned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-semibold" data-testid={`kw-assigned-${p.id}`}>KW Assigned</span>}
                      {!p.kw_assigned && p.kw_unassigned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 font-semibold" data-testid={`kw-unassigned-${p.id}`}>KW Unassigned</span>}
                      {requestedProjectIds.has(p.id) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold">Requested</span>}
                      {p.archived && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 border border-slate-300 font-semibold">Archived</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => toggleKwAssigned(p)}
                        data-testid={`kp-mark-assigned-${p.id}`}
                        className={`text-[10px] px-2 py-1 rounded font-semibold border transition ${p.kw_assigned ? "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200" : "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"}`}
                      >
                        {p.kw_assigned ? "Unmark & Reopen" : "Mark KW Assigned"}
                      </button>
                      <Link to={`/projects/${p.id}`} className="inline-flex items-center gap-1 text-[11px] bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded font-semibold" data-testid={`kp-open-${p.id}`}>
                        Open <ArrowUpRight className="h-3 w-3" />
                      </Link>
                      {isAdminOrSeoDA && (
                        <button onClick={() => deleteProject(p)} title="Delete project" className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`kp-delete-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


export default function SEOFolder() {
  const { folderId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pushed, setPushed] = useState({});
  const [manageOpen, setManageOpen] = useState(false);

  const loadFolders = useCallback(async () => {
    try {
      const r = await api.get("/seo/folders");
      setFolders(r.data || []);
      const cur = (r.data || []).find((f) => f.id === folderId);
      if (cur) {
        setFolder(cur);
      } else if (!folderId) {
        // Only auto-select the first folder when the URL has NO folderId (bare /seo route).
        // If the URL DOES contain a folderId that isn't in the list, keep the URL as-is
        // and show an "access denied" state — do NOT silently redirect to someone else's folder.
        const first = (r.data || [])[0];
        setFolder(first);
        if (first) nav(`/seo/folder/${first.id}`, { replace: true });
      } else {
        setFolder(null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [folderId, nav]);

  const loadItems = useCallback(async (fid) => {
    try {
      const r = await api.get(`/seo/items?folder_id=${fid}`);
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { if (folder?.id) loadItems(folder.id); }, [folder?.id, loadItems]);

  const patchItem = async (id, patch) => {
    // optimistic
    setItems((all) => all.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    try {
      const r = await api.patch(`/seo/items/${id}`, patch);
      setItems((all) => all.map((x) => (x.id === id ? r.data : x)));
    } catch (e) {
      toast.error(formatApiError(e));
      // reload to correct
      if (folder?.id) loadItems(folder.id);
    }
  };

  const addItem = async () => {
    try {
      const r = await api.post("/seo/items", { folder_id: folder.id, project_name: "New project" });
      setItems((all) => [...all, r.data]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await api.delete(`/seo/items/${id}`);
      setItems((all) => all.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 43 — pushToContent moved to OnboardingTable; SEOFolder no longer
  // needs the handler since the row-edit dialog owns the push flow.

  // Iter 43 — incubation removed. Push-to-content is now an anytime action,
  // available on the row-edit dialog. endIncubation / reopenIncubation
  // handlers deleted along with the endpoints.

  const createFolder = async () => {
    const name = window.prompt("New folder name:");
    if (!name) return;
    try {
      const r = await api.post("/seo/folders", { name });
      toast.success("Folder created");
      setFolders((all) => [...all, r.data]);
      nav(`/seo/folder/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteFolder = async () => {
    if (!folder || folder.system) return;
    if (!window.confirm(`Delete folder "${folder.name}" and all its items?`)) return;
    try {
      await api.delete(`/seo/folders/${folder.id}`);
      toast.success("Folder deleted");
      const remaining = folders.filter((f) => f.id !== folder.id);
      setFolders(remaining);
      nav(`/seo/folder/${remaining[0]?.id || ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Filtering & derived
  const statusOptions = folder?.status_options || [];
  const hostedOptions = folder?.hosted_on_options || [];
  const filteredItems = useMemo(() => {
    let list = items;
    if (statusFilter !== "all") list = list.filter((x) => x.status === statusFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((x) =>
        (x.project_name || "").toLowerCase().includes(s) ||
        (x.example_site || "").toLowerCase().includes(s) ||
        (x.username || "").toLowerCase().includes(s),
      );
    }
    return list;
  }, [items, q, statusFilter]);

  const endedIncubation = useMemo(() => [], []); // Iter 43 — feature removed; kept as [] to preserve JSX signatures elsewhere in case of stale refs.

  if (!folder) {
    return <PageSkeleton title cards={0} rows={6} filters />;
  }

  const isOnboarding = folder.type === "onboarding";
  const isKP = folder.type === "keyword_planning";
  const canEdit = folder.can_edit_items;
  const canManage = folder.can_manage;
  // Iter 36.9.8 — when a folder has an embed_url (typically a Google
  // Sheets share link), render it as an iframe in place of the native
  // spreadsheet. Managers get a small toolbar to swap or clear the embed.
  const hasEmbed = !!(folder.embed_url && folder.embed_url.trim());
  const clearEmbed = async () => {
    if (!window.confirm("Remove the embedded spreadsheet? The native folder table will return.")) return;
    try {
      const r = await api.patch(`/seo/folders/${folder.id}`, { embed_url: "" });
      setFolder(r.data);
      setFolders((all) => all.map((x) => (x.id === r.data.id ? r.data : x)));
      toast.success("Embedded spreadsheet removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const changeEmbed = async () => {
    const url = window.prompt("Google Sheets URL to embed:", folder.embed_url || "");
    if (url === null) return; // cancelled
    try {
      const r = await api.patch(`/seo/folders/${folder.id}`, { embed_url: url.trim() });
      setFolder(r.data);
      setFolders((all) => all.map((x) => (x.id === r.data.id ? r.data : x)));
      toast.success(url.trim() ? "Embed updated" : "Embed cleared");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="w-full max-w-[1600px] min-w-0 mx-auto p-6 space-y-5">
      {/* Header — Iter 45.11 polish: bigger, prettier hero row. */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-200 flex items-center justify-center shrink-0 shadow-sm">
            <FolderOpen className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-purple-500 font-bold flex items-center gap-1.5">
              SEO · {folder.type === "onboarding" ? "Onboarding" : folder.type === "keyword_planning" ? "Keyword Planning" : "Custom"}
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-900 leading-tight">{folder.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <Button variant="outline" onClick={() => setManageOpen(true)} className="h-9 text-xs border-slate-200 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700" data-testid="manage-folder-btn">
                <Settings className="h-3.5 w-3.5 mr-1.5" /> Manage folder
              </Button>
              {!folder.system && (
                <Button variant="outline" onClick={deleteFolder} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-folder-btn">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Folder tabs removed — each folder is a standalone unit; use sidebar to switch. */}

      {/* Iter 36.9.8 — embedded spreadsheet takes over the folder view when
          embed_url is set. Managers see a small toolbar for edit/clear. */}
      {hasEmbed ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-purple-50 border border-purple-200 rounded-md px-3 py-2">
            <ExternalLink className="h-3.5 w-3.5 text-purple-500" />
            <span className="flex-1 truncate">Embedded spreadsheet: <a href={folder.embed_url} target="_blank" rel="noreferrer" className="text-purple-700 hover:underline">{folder.embed_url}</a></span>
            {canManage && (
              <>
                <Button variant="outline" size="sm" onClick={changeEmbed} className="h-7 text-xs" data-testid="embed-change-btn">Change URL</Button>
                <Button variant="outline" size="sm" onClick={clearEmbed} className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="embed-clear-btn">Remove embed</Button>
              </>
            )}
          </div>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" style={{ height: "calc(100vh - 220px)", minHeight: 420 }}>
            <iframe
              src={folder.embed_url}
              title={`${folder.name} — embedded spreadsheet`}
              className="w-full h-full border-0"
              allow="clipboard-read; clipboard-write"
              data-testid="embed-iframe"
            />
          </div>
        </div>
      ) : null}

      {/* Iter 43 — incubation banner removed. Push-to-Content is now an
          anytime action available on the row-edit dialog. */}

      {!hasEmbed && (isOnboarding ? (
        <OnboardingTable
          folder={folder}
          items={items}
          setItems={setItems}
          setFolder={(f) => { setFolder(f); setFolders((all) => all.map((x) => x.id === f.id ? f : x)); }}
          reload={() => folder?.id && loadItems(folder.id)}
        />
      ) : isKP ? (
        <KeywordPlanningView folder={folder} />
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="folder-empty-state">
          <Sparkles className="h-6 w-6 mx-auto text-slate-400 mb-2" />
          <div className="text-sm font-semibold text-slate-700">{folder.name} — coming soon</div>
          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">This folder is a placeholder. Admin or Department Admin can configure columns and start adding items.</div>
        </div>
      ))}

      <ManageFolderDialog folder={folder} open={manageOpen} onClose={() => setManageOpen(false)} onSaved={(f) => { setFolder(f); setFolders((all) => all.map((x) => x.id === f.id ? f : x)); }} />
    </div>
  );
}
